import { ratingOptions as scorecardOptions } from '@/constants/scorecard-configs';
import _ from 'lodash';
import { overallEvaluationStyles } from '@/hooks/styles/overallEvaluvationStyles';
import { http } from '@/lib/ats-https';
import { DEFAULT_ATTACHMENT_FILES } from '@/constants/attachment-files';
import { sanitizeDom } from '@/lib/purify';

const formatFeedback = (feedback) => {
  if (!feedback || typeof feedback !== 'string') return feedback;
  const trimmed = feedback.trim();

  if (trimmed.startsWith('<')) {
    return sanitizeDom(feedback);
  }

  const lines = trimmed.split('\n').filter((line) => line.trim());
  if (lines.length === 0) {
    return sanitizeDom(feedback) ?? feedback;
  }

  const hasBulletFormat = lines.some((line) => /^-\s+/.test(line.trim()));
  if (hasBulletFormat) {
    const listHtml = lines
      .map((line) => `<li>${line.replace(/^-\s*/, '').trim()}</li>`)
      .join('');
    return sanitizeDom(`<ul>${listHtml}</ul>`) ?? feedback;
  }

  return sanitizeDom(feedback) ?? feedback;
};

const labelStyles = {
  size: 'sm-semi-bold',
  color: 'secondary-900',
  minWidth: 'unset',
  width: '100%',
};

const formControlContainerStyles = {
  paddingBottom: '20px',
  borderBottom: '1px solid #E9EBF4',
  flexDir: 'column',
};

const commonEditorLabelStyles = () => ({
  ...labelStyles,
  marginBottom: '0px',
  marginRight: '0px',
});

const editorStyles = {
  enableCustomVariables: false,
  customEditorClassName: 'scorecard-editor-class',
  containerStyles: { width: '100%' },
  editorContainerStyles: { border: 'none' },
  editorWraperStyles: {
    padding: '16px',
    border: '1px solid',
    borderColor: 'border-color',
    borderRadius: 'sm',
    overflow: 'hidden',
  },
  editorStyles: { padding: '0px' },
  toolbarWrapperStyles: { marginLeft: '-10px' },
  pluginAlignment: 'bottom',
};

const radioGroupStyles = {
  styles: { direction: 'column', ml: '0px', gap: '8px' },
  radioStyles: { variant: 'ats-primary-sm' },
  radioWrapperStyles: {
    flexDir: 'row',
    flexWrap: 'wrap',
    gap: '12px',
    width: '100%',
    minWidth: 0,
  },
  radioLabelStyles: {
    size: 'sm',
    lineHeight: '20px',
    mb: '0px',
    whiteSpace: 'normal',
  },
  radioIconStyles: { width: '24px', height: '24px' },
  labelStyles: { ...labelStyles, marginBottom: '15px' },
  subLabelStyles: { marginBottom: '15px' },
  formControlContainerStyles,
};

const checkboxGroupStyles = {
  styles: { direction: 'column', ml: '0px', gap: '8px' },
  checkboxStyles: { variant: 'ats-primary-sm' },
  checkboxWrapperStyles: {
    flexDir: 'row',
    flexWrap: 'wrap',
    gap: '12px',
    width: '100%',
    minWidth: 0,
  },
  checkboxLabelStyles: {
    size: 'sm',
    lineHeight: '20px',
    mb: '0px',
    whiteSpace: 'normal',
  },
  labelStyles: { ...labelStyles, marginBottom: '15px' },
  subLabelStyles: { marginBottom: '15px' },
  formControlContainerStyles,
};

const yesNoOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const typeMap = {
  star: {
    type: 'star',
    formControlContainerStyles,
    labelStyles: { ...labelStyles, marginBottom: '12px' },
    subLabelStyles: { marginBottom: '12px' },
    errorMessage: 'This is required field',
  },
  one_to_ten: {
    type: 'rating',
    min: 1,
    max: 10,
    formControlContainerStyles,
    labelStyles: { ...labelStyles, marginBottom: '12px' },
    subLabelStyles: { marginBottom: '12px' },
    errorMessage: 'This is required field',

    optionWrapperStyles: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      width: '100%',
      minWidth: 0,
    },
  },
  yes_no: {
    type: 'radio',
    options: yesNoOptions,
    formControlContainerStyles,
    radioStyles: { variant: 'ats-primary-sm' },
    labelStyles: { ...labelStyles, marginBottom: '12px' },
    subLabelStyles: { marginBottom: '12px' },
    errorMessage: 'This is required field',
  },
  open_question: {
    type: 'editor',
    errorMessage: 'This is required field',
    ...editorStyles,
    placeholder: 'Answer here',
    hasCodeBlock: true,
    resize: true,
  },
  multiple_choice: {
    type: 'radio',
    ...radioGroupStyles,
    errorMessage: 'This is required field',
  },
  checkbox: {
    type: 'checkboxGroup',
    ...checkboxGroupStyles,
    isMulti: true,
    errorMessage: 'This is required field',
  },
};

const uploadAssessment = async ({
  atsApplicationId,
  scorecardId,
  attachments = [],
  attachmentType,
  question,
}) => {
  try {
    const formData = new FormData();
    attachments.forEach((file) => {
      formData.append('attachments[]', file);
    });
    formData.append('attachment_type', attachmentType);
    formData.append('question', question);

    const response = await http.put(
      `/ats_applications/${atsApplicationId}/ats_scorecards/${scorecardId}/upload_attachment`,
      formData
    );

    if (response?.errors?.length) {
      return { error: response?.errors };
    }
    return response.data;
  } catch (err) {
    return {
      error: err.response?.data,
    };
  }
};

const deleteAttachment = async ({
  attachmentId,
  atsApplicationId,
  scorecardId,
  question = null,
  attachmentType,
}) => {
  try {
    const attachment = {
      attachment_id: attachmentId,
      attachment_type: attachmentType,
    };

    if (question) {
      attachment.question = question;
    }
    const response = await http.delete(
      `/ats_applications/${atsApplicationId}/ats_scorecards/${scorecardId}/delete_attachment`,
      { data: attachment }
    );
    if (response?.errors?.length) {
      return { status: 'failure', errors: response?.errors };
    }
    return { status: 'success', data: response.data };
  } catch (err) {
    return {
      status: 'failure',
      error: err.response?.data,
    };
  }
};

const API_SERVICE = {
  uploadAssessment,
  deleteAttachment,
};

const handleAttachmentUpload = async (
  files,
  scorecard,
  question,
  attachmentKey,
  attachmentType,
  updateInitialValues,
  showToast
) => {
  // Filter out already uploaded files
  const newFiles = files.filter((file) => !file?.id);

  if (newFiles.length === 0) return;

  const { id: scorecardId, ats_application_id: atsApplicationId } =
    scorecard || {};

  const response = await API_SERVICE.uploadAssessment({
    atsApplicationId,
    scorecardId,
    attachments: newFiles,
    question,
    attachmentType,
  });

  if (response?.attachments) {
    updateInitialValues?.({
      type: 'add',
      attachments: response.attachments,
      attachmentKey,
    });
  } else {
    console.error(
      'Attachment upload failed: No attachments in response',
      response
    );

    // Mark the failed files with hasError flag
    const failedFiles = newFiles.map((file) => {
      // For File objects, we need to assign hasError directly
      if (file instanceof File) {
        const fileWithError = file;
        fileWithError.hasError = true;
        return fileWithError;
      }
      // For regular objects, spread works fine
      return file;
    });

    updateInitialValues?.({
      type: 'updateError',
      attachments: failedFiles,
      attachmentKey,
    });

    showToast?.({
      title: 'File size limit exceeded',
      description:
        'The total size of all attachments should be less than 50 MB per scorecard. Please remove some files or choose smaller files.',
      status: 'error',
      isClosable: true,
    });
  }
};

const handleAttachmentDelete = async (
  scorecard,
  question,
  attachmentId,
  attachmentType,
  attachmentKey,
  updateInitialValues,
  showToast
) => {
  const { id: scorecardId, ats_application_id: atsApplicationId } =
    scorecard || {};

  const response = await API_SERVICE.deleteAttachment({
    atsApplicationId,
    scorecardId,
    question,
    attachmentId,
    attachmentType,
  });

  if (response?.status === 'success') {
    updateInitialValues?.({
      type: 'remove',
      attachmentKey,
      attachmentId,
    });
  } else {
    console.error('Attachment deletion failed:', response);
    if (
      response?.errors?.[0] === 'You are not authorized to perform this action'
    ) {
      showToast?.({
        title: 'You don’t have permission to delete this attachment',
        status: 'error',
      });
    }
  }
};

export const ratingOptions = scorecardOptions;

export const getScoreCardFormConfig = ({
  questions = [],
  canSubmit,
  isEdit,
  initialValues = {},
  scoreCardDetails,
  canFillForOthers: _canFillForOthers = false,
  updateInitialValues,
  showToast,
  isMobile = false,
}) => {
  const overallEvaluationStyle = overallEvaluationStyles(isMobile);
  const { feedback_description, rating_description } = initialValues;
  const setRatingOptions = (ratingOptions, selectedItemRef) => {
    ratingOptions.forEach((item) => {
      if (item?.value === selectedItemRef) {
        item['radioIconStyles'] = {
          color: item.selectedColor,
          className: `checked_${selectedItemRef}`,
        };
        item['radioWrapperStyles'] = {
          background: item.bgColor,
          borderColor: item.selectedColor,
          color: item.selectedColor,
        };
        item['radioLabelStyles'] = {
          color: item.selectedColor,
        };
      } else {
        item['radioIconStyles'] = {
          className: 'primary-900',
        };
        item['radioWrapperStyles'] = {
          background: 'white',
          borderColor: 'border-color',
          color: 'primary-900',
        };
        item['radioLabelStyles'] = {
          color: 'primary-900',
        };
      }
    });
  };

  const ratingOptions = _.cloneDeep(scorecardOptions);
  setRatingOptions(ratingOptions, initialValues?.rating);

  const isFeedbackRequired = scoreCardDetails?.is_feedback_mandatory ?? true;

  const questionFields = questions?.map((item, idx) => {
    const { mandatory, type, question, options, description } = item || {};
    const typeFields = typeMap?.[type];
    const isEditor = type === 'open_question';

    const baseField = {
      name: idx + 1,
      label: question,
      options: options?.map((item) => ({ value: item, label: item })),
      isRequired: mandatory === 'true' || mandatory === true ? true : false,
      disabled: !canSubmit,
      editable: isEdit,
      subLabel: description,
      labelStyles: commonEditorLabelStyles(isEditor && description),
      ...typeFields,
    };

    // Add attachment support for open_question type
    if (type === 'open_question') {
      const attachmentKey = `${idx + 1}_attachments`;
      return {
        ...baseField,
        pluginsConfig: ['attachment'],
        attachmentKey: attachmentKey,
        attachmentFiles: DEFAULT_ATTACHMENT_FILES.join(','),
        attachmentTooltipMessage:
          'Upload files up to a max limit of 50 MB per scorecard',
        totalSizeLimitMB: 49.9,
        onAttachmentChange: async (attachments) => {
          // console.log(`Question attached files:`, attachments);
          handleAttachmentUpload(
            attachments,
            scoreCardDetails,
            item.question,
            attachmentKey,
            'question',
            updateInitialValues,
            showToast
          );
        },
        onAttachmentRemove: async (removedFile) => {
          handleAttachmentDelete(
            scoreCardDetails,
            item.question,
            removedFile.id,
            'question',
            attachmentKey,
            updateInitialValues,
            showToast
          );
        },
      };
    }

    return baseField;
  });

  return [
    ...questionFields,
    {
      name: `feedback`,
      enableCustomVariables: false,
      type: 'editor',
      isRequired: isFeedbackRequired,
      label: 'Feedback',
      placeholder: 'Write your feedback about the candidate here',
      editable: isEdit,
      subLabel: feedback_description,
      hasCodeBlock: true,
      resize: true,
      labelStyles: commonEditorLabelStyles(feedback_description),
      ...editorStyles,
      pluginsConfig: ['attachment'],
      attachmentKey: 'feedback_attachments',
      attachmentFiles: DEFAULT_ATTACHMENT_FILES.join(','),
      attachmentTooltipMessage:
        'Upload files up to a max limit of 50 MB per scorecard',
      totalSizeLimitMB: 49.9,
      onAttachmentChange: async (files) => {
        console.log(`Feedback attached files:`, files);
        handleAttachmentUpload(
          files,
          scoreCardDetails,
          'feedback',
          'feedback_attachments',
          'feedback',
          updateInitialValues,
          showToast
        );
      },
      onAttachmentRemove: async (removedFile) => {
        console.log('Feedback removed file:', removedFile);
        handleAttachmentDelete(
          scoreCardDetails,
          null,
          removedFile.id,
          'feedback',
          'feedback_attachments',
          updateInitialValues,
          showToast
        );
      },
    },
    ...(ratingOptions.length > 0 &&
    !(initialValues?.rating === null && !canSubmit)
      ? [
          {
            name: 'rating',
            type: 'radio',
            label: 'Overall evaluation',
            options: ratingOptions,
            isRequired: true,
            disabled: !canSubmit,
            editable: isEdit,
            subLabel: rating_description,
            ...overallEvaluationStyle,
            styles: {
              ...overallEvaluationStyle.styles,
              direction: isMobile ? 'column' : 'row',
            },
            onChange: (selectedItemRef) => {
              setRatingOptions(ratingOptions, selectedItemRef);
            },
          },
        ]
      : []),
  ];
};

export const getInitialValues = ({ isSubmitted, values }) => {
  if (isSubmitted) {
    const {
      rating,
      feedback,
      questions,
      feedback_description,
      rating_description,
      attachments = [],
      feedback_attachments = [],
    } = values || {};
    const questionAnswers = questions?.reduce((acc, curr, idx) => {
      const {
        answer,
        type,
        attachments: questionAttachments = [],
      } = curr || {};
      const formattedAnswer =
        type === 'open_question'
          ? (formatFeedback(answer) ?? answer ?? '')
          : (answer ?? '');
      return {
        ...acc,
        [idx + 1]: formattedAnswer,
        [`${idx + 1}_attachments`]: questionAttachments || [],
      };
    }, {});
    return {
      rating,
      feedback: formatFeedback(feedback) ?? feedback,
      feedback_description,
      rating_description,
      attachments: attachments || [],
      feedback_attachments: feedback_attachments || [],
      ...questionAnswers,
    };
  }
  return {};
};
